import { useCallback, useEffect, useRef, useState } from 'react';
import { AlertTriangle, ArrowLeft, CheckCircle2, Flag, Loader2, RefreshCw, XCircle } from 'lucide-react';
import { Link, useSearchParams } from 'react-router-dom';

import { DeliverySections, ExceptionSections } from '../../components/delivery/LogisticsSections';
import { useBusinessAuth } from '../../context/BusinessAuthContext';
import { businessDeliveryOperationsService as deliveryApi } from '../../services/businessDeliveryOperationsService';
import ShopSelect from '../../../components/ShopSelect';
import { Button, PageHeader } from '../../../components/ui';

// Delivery is complete as soon as a rider's proof is finalized; a review never
// gates it. One page serves two intents, chosen by how the reader arrived:
//   Exceptions -> Flagged proofs   the few the server flags for review
//   Delivery   -> Delivery proofs  every proof, kept as a record (?show=all)
// A link with neither stays on the flagged list, as it always has.
const PAGE_SIZE = 25;
const NOTE_MIN = 10;
const NOTE_MAX = 500;
const STATUS_LABELS = Object.freeze({ FINALIZED: 'Not reviewed', APPROVED: 'Approved', REJECTED: 'Rejected' });
const RECIPIENT_LABELS = Object.freeze({
  CUSTOMER: 'the customer', HOUSEHOLD_MEMBER: 'a household member',
  GUARD_RECEPTION: 'a guard or reception desk', AUTHORIZED_REPRESENTATIVE: 'an authorized representative',
});
const GPS_EXCEPTION_COPY = Object.freeze({
  GPS_UNAVAILABLE: 'The rider\'s phone could not get a location when the photo was taken.',
  GPS_PERMISSION_DENIED: 'Location access was turned off on the rider\'s phone.',
  GPS_DEVICE_ERROR: 'The rider\'s phone reported a location error.',
  GPS_POOR_ACCURACY_ACCEPTED: 'The location reading was too imprecise to rely on.',
});
const TIER_COPY = Object.freeze({
  DISTANCE_REVIEW: 'The photo was taken far from the delivery address.',
  GPS_EXCEPTION_REVIEW: 'The photo\'s location could not be confirmed.',
});
const dateTime = (value) => value ? new Intl.DateTimeFormat('en-PH', { dateStyle: 'medium', timeStyle: 'short' }).format(new Date(value)) : 'Not recorded';
const meters = (value) => value == null ? null : Number(value) >= 1000 ? `${(Number(value) / 1000).toFixed(1)} km` : `${Math.round(Number(value))} m`;
const positiveId = (value) => /^\d+$/.test(value || '') ? Number(value) : null;
const focusRing = 'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus';

export function receivedBy(proof = {}) {
  const who = RECIPIENT_LABELS[proof.recipientType];
  if (proof.recipientName) return who ? `${proof.recipientName} (${who})` : proof.recipientName;
  return who ? who.replace(/^./, (c) => c.toUpperCase()) : null;
}

export function proofFlagReasons(proof = {}) {
  const reasons = [];
  if (proof.gpsExceptionCode) reasons.push(GPS_EXCEPTION_COPY[proof.gpsExceptionCode] || 'The photo\'s location could not be confirmed.');
  if (TIER_COPY[proof.locationEvidenceTier] && !(proof.gpsExceptionCode && proof.locationEvidenceTier === 'GPS_EXCEPTION_REVIEW')) {
    reasons.push(TIER_COPY[proof.locationEvidenceTier]);
  }
  return reasons;
}

function Fact({ term, children }) {
  if (children == null || children === '') return null;
  return <div className="min-w-0"><dt className="text-xs text-neutral-600 dark:text-neutral-400">{term}</dt><dd className="mt-0.5 break-words text-sm font-medium">{children}</dd></div>;
}

function StatusPill({ status }) {
  const tone = status === 'APPROVED' ? 'bg-success-subtle text-success'
    : status === 'REJECTED' ? 'bg-danger-subtle text-danger'
      : 'bg-neutral-100 text-neutral-800 dark:bg-white/10 dark:text-neutral-100';
  return <span className={`inline-flex shrink-0 rounded-full px-2.5 py-1 text-xs font-semibold ${tone}`}>{STATUS_LABELS[status] || 'Not reviewed'}</span>;
}

function FlagPill() {
  return <span className="inline-flex shrink-0 items-center gap-1 rounded-full bg-warning-subtle px-2.5 py-1 text-xs font-semibold text-warning"><Flag className="h-3 w-3" aria-hidden="true" />Flagged</span>;
}

export default function DeliveryProofReview() {
  const auth = useBusinessAuth();
  const businessId = Number(auth.businessId);
  const owner = String(auth.role || '').toUpperCase() === 'OWNER';
  const canReadParcels = owner || auth.hasPermission?.('delivery.dispatch.read.business');
  const [params, setParams] = useSearchParams();
  const showAll = params.get('show') === 'all';
  const status = showAll && STATUS_LABELS[params.get('status')] ? params.get('status') : 'FINALIZED';
  const proofId = positiveId(params.get('proof'));

  const [items, setItems] = useState([]);
  const [nextCursor, setNextCursor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [listError, setListError] = useState('');
  const [detail, setDetail] = useState(null);
  const [detailState, setDetailState] = useState('idle');
  const [photoFailed, setPhotoFailed] = useState(false);
  const [note, setNote] = useState('');
  const [noteTouched, setNoteTouched] = useState(false);
  const [busy, setBusy] = useState(false);
  const [decisionError, setDecisionError] = useState('');
  const [notice, setNotice] = useState('');
  const listSequence = useRef(0);
  const detailSequence = useRef(0);
  const detailHeading = useRef(null);

  const updateParams = useCallback((changes) => setParams((current) => {
    const next = new URLSearchParams(current);
    Object.entries(changes).forEach(([key, value]) => {
      if (value == null || value === '') next.delete(key);
      else next.set(key, String(value));
    });
    return next;
  }), [setParams]);

  const load = useCallback(async ({ cursor = null } = {}) => {
    const sequence = ++listSequence.current;
    if (cursor) setLoadingMore(true); else setLoading(true);
    setListError('');
    try {
      const result = await deliveryApi.proofs(businessId, { status, limit: PAGE_SIZE, ...(cursor ? { cursor } : {}) });
      if (sequence !== listSequence.current) return;
      const page = result?.items || [];
      setItems((current) => (cursor ? [...current, ...page] : page));
      setNextCursor(result?.nextCursor ?? null);
    } catch (requestError) {
      if (sequence !== listSequence.current) return;
      setListError(requestError?.response?.status === 403
        ? 'You do not have permission to see delivery proofs.'
        : 'Delivery proofs could not be loaded. Check your connection and try again.');
      if (!cursor) { setItems([]); setNextCursor(null); }
    } finally {
      if (sequence === listSequence.current) { setLoading(false); setLoadingMore(false); }
    }
  }, [businessId, status]);
  useEffect(() => { load(); }, [load]);

  const loadDetail = useCallback(async () => {
    const sequence = ++detailSequence.current;
    if (!proofId) { setDetail(null); setDetailState('idle'); return; }
    setDetailState('loading'); setPhotoFailed(false);
    try {
      const proof = await deliveryApi.proof(businessId, proofId);
      if (sequence !== detailSequence.current) return;
      setDetail(proof); setDetailState('ready');
    } catch (requestError) {
      if (sequence !== detailSequence.current) return;
      setDetail(null);
      setDetailState(requestError?.response?.status === 404 ? 'missing' : 'error');
    }
  }, [businessId, proofId]);
  useEffect(() => { setNote(''); setNoteTouched(false); setDecisionError(''); setNotice(''); loadDetail(); }, [loadDetail]);
  // Opening a proof moves focus to it, so a keyboard or phone user is not left
  // at the top of a list while the proof appears somewhere else.
  useEffect(() => { if (detailState === 'ready') detailHeading.current?.focus(); }, [detailState, proofId]);

  const visible = showAll ? items : items.filter((item) => item.reviewRequired);
  const listed = items.find((item) => item.id === proofId) || null;
  const flagReasons = detail ? proofFlagReasons(detail) : [];
  const trimmedNote = note.replace(/\s+/g, ' ').trim();
  const noteTooShort = trimmedNote.length < NOTE_MIN;

  const decide = async (decision) => {
    if (busy) return;
    if (decision === 'REJECTED' && noteTooShort) { setNoteTouched(true); return; }
    setBusy(true); setDecisionError(''); setNotice('');
    try {
      await deliveryApi.reviewProof(businessId, detail.id, decision, trimmedNote || undefined);
      setNotice(decision === 'APPROVED' ? 'Approval recorded.' : 'Rejection recorded with your note.');
      setNote(''); setNoteTouched(false);
      await Promise.all([load(), loadDetail()]);
    } catch (requestError) {
      setDecisionError(requestError?.response?.status === 409
        ? 'Someone else already reviewed this proof. The latest decision is shown.'
        : requestError?.response?.status === 403
          ? 'You do not have permission to review delivery proofs.'
          : 'The decision could not be saved. Check your connection and try again.');
      if (requestError?.response?.status === 409) await Promise.all([load(), loadDetail()]);
    } finally { setBusy(false); }
  };

  const title = listed?.orderReference || (proofId ? `Proof #${proofId}` : '');

  return (
    <main className="space-y-5 pb-10">
      <PageHeader
        title={showAll ? 'Delivery proofs' : 'Flagged proofs'}
        description={showAll
          ? "The photo a rider saves at each delivery, kept as a record. A delivery is complete as soon as the photo is saved, so nothing here is waiting for approval."
          : 'Proofs whose recorded location did not match the delivery address closely enough. The delivery itself is already complete; this is a check of the evidence.'}
        actions={<Button size="sm" icon={RefreshCw} onClick={() => { load(); loadDetail(); }}>Refresh</Button>}
        navigation={showAll ? <DeliverySections current="proofs" /> : <ExceptionSections current="proofs" />}
      />

      <div className="grid items-start gap-5 lg:grid-cols-[minmax(0,22rem)_minmax(0,1fr)]">
        <section aria-label="Proof list" className={`overflow-hidden rounded-panel border border-default bg-surface ${proofId ? 'hidden lg:block' : ''}`}>
          {showAll && <div className="border-b border-neutral-200 p-3 dark:border-neutral-800">
            {(
              <div className="flex items-center justify-between gap-2 text-sm font-medium">
                <span>Review status</span>
                <ShopSelect
                  value={status}
                  onValueChange={(value) => updateParams({ status: value, proof: null })}
                  options={Object.entries(STATUS_LABELS).map(([value, text]) => ({ value, label: text }))}
                  ariaLabel="Review status"
                  align="end"
                  minPanelWidth={144}
                  triggerClassName="min-h-11 w-36 rounded-control px-3 text-sm font-medium"
                  panelClassName="py-1"
                />
              </div>
            )}
          </div>}

          {listError && <p role="alert" className="flex gap-2 border-b border-warning bg-warning-subtle p-3 text-sm text-warning"><AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />{listError}</p>}
          {loading ? (
            <p role="status" className="flex items-center gap-2 p-4 text-sm text-neutral-600 dark:text-neutral-300"><Loader2 className="h-4 w-4 animate-spin motion-reduce:animate-none" aria-hidden="true" />Loading proofs…</p>
          ) : visible.length ? (
            <ul className="divide-y divide-neutral-200 dark:divide-neutral-800">
              {visible.map((proof) => {
                const selected = proof.id === proofId;
                return (
                  <li key={proof.id}>
                    <button type="button" aria-current={selected ? 'true' : undefined} onClick={() => updateParams({ proof: proof.id })}
                      className={`flex w-full items-start justify-between gap-3 px-4 py-3 text-left ${focusRing} focus-visible:ring-inset ${selected ? 'bg-accent-subtle' : 'hover:bg-neutral-50 dark:hover:bg-white/5'}`}>
                      <span className="min-w-0">
                        <span className="block truncate font-semibold">{proof.orderReference || `Proof #${proof.id}`}</span>
                        <span className="block truncate text-sm text-neutral-600 dark:text-neutral-300">{proof.riderName} · {dateTime(proof.finalizedAt || proof.createdAt)}</span>
                      </span>
                      {proof.reviewRequired ? <FlagPill /> : showAll && status !== 'FINALIZED' ? <StatusPill status={proof.status} /> : null}
                    </button>
                  </li>
                );
              })}
            </ul>
          ) : !listError ? (
            <p className="flex items-start gap-2 p-4 text-sm text-neutral-700 dark:text-neutral-200">
              {!showAll && <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-success" aria-hidden="true" />}
              {showAll ? 'No proofs have this review status.' : `No proof is flagged in the ${items.length ? `latest ${items.length} ` : ''}deliveries checked.`}
            </p>
          ) : null}
          {!loading && nextCursor != null && (
            <div className="border-t border-neutral-200 p-3 dark:border-neutral-800">
              <button type="button" disabled={loadingMore} onClick={() => load({ cursor: nextCursor })} className={`inline-flex min-h-11 w-full items-center justify-center gap-2 rounded-xl border border-neutral-300 px-4 text-sm font-semibold hover:bg-neutral-100 disabled:opacity-50 dark:border-neutral-700 dark:hover:bg-neutral-800 ${focusRing}`}>
                {loadingMore && <Loader2 className="h-4 w-4 animate-spin motion-reduce:animate-none" aria-hidden="true" />}
                {showAll ? 'Show older proofs' : 'Check older proofs'}
              </button>
            </div>
          )}
        </section>

        <section aria-label="Proof details" className={`min-w-0 ${proofId ? '' : 'hidden lg:block'}`}>
          {proofId && <button type="button" onClick={() => updateParams({ proof: null })} className={`mb-3 inline-flex min-h-11 items-center gap-2 text-sm font-semibold text-secondary hover:text-accent lg:hidden ${focusRing}`}><ArrowLeft className="h-4 w-4" aria-hidden="true" />Back to proofs</button>}
          {!proofId && <p className="rounded-2xl border border-dashed border-neutral-300 p-6 text-sm text-neutral-600 dark:border-neutral-700 dark:text-neutral-300">Choose a proof to see the photo, who received the parcel, and where it was taken.</p>}
          {detailState === 'loading' && <p role="status" className="flex items-center gap-2 rounded-2xl border border-neutral-200 p-6 text-sm text-neutral-600 dark:border-neutral-800 dark:text-neutral-300"><Loader2 className="h-4 w-4 animate-spin motion-reduce:animate-none" aria-hidden="true" />Opening proof…</p>}
          {detailState === 'missing' && <p role="alert" className="rounded-2xl border border-warning bg-warning-subtle p-4 text-sm text-warning">That proof was not found. It may belong to another business, or the link may be wrong.</p>}
          {detailState === 'error' && <div role="alert" className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-warning bg-warning-subtle p-4 text-sm text-warning"><span>That proof could not be opened.</span><button type="button" onClick={loadDetail} className="min-h-11 rounded-xl border border-warning px-4 font-semibold focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus">Try again</button></div>}

          {detailState === 'ready' && detail && (
            <article className="overflow-hidden rounded-2xl border border-neutral-200 bg-white dark:border-neutral-800 dark:bg-neutral-950">
              <div className="flex flex-wrap items-start justify-between gap-3 border-b border-neutral-200 p-4 dark:border-neutral-800">
                <div className="min-w-0">
                  <h2 ref={detailHeading} tabIndex={-1} className="break-words text-lg font-semibold focus:outline-none">{title}</h2>
                  <p className="mt-0.5 text-sm text-neutral-600 dark:text-neutral-300">{listed?.riderName ? `${listed.riderName} · ` : ''}Photo taken {dateTime(detail.deviceCapturedAt || detail.finalizedAt)}</p>
                </div>
                <div className="flex flex-wrap gap-2">{detail.reviewRequired && <FlagPill />}<StatusPill status={detail.status} /></div>
              </div>

              {notice && <p role="status" aria-live="polite" className="border-b border-success bg-success-subtle px-4 py-3 text-sm text-success">{notice}</p>}

              <div className="grid gap-5 p-4 xl:grid-cols-[minmax(0,1.1fr)_minmax(0,1fr)]">
                <div>
                  {detail.viewUrl && !photoFailed ? (
                    <a href={detail.viewUrl} target="_blank" rel="noopener noreferrer" className={`block overflow-hidden rounded-xl bg-neutral-100 dark:bg-neutral-900 ${focusRing}`}>
                      <img src={detail.viewUrl} alt={`Proof of delivery for ${title}`} referrerPolicy="no-referrer" onError={() => setPhotoFailed(true)} className="mx-auto max-h-[28rem] w-full object-contain" />
                      <span className="sr-only">Open the photo at full size in a new tab</span>
                    </a>
                  ) : (
                    <div className="rounded-xl border border-dashed border-neutral-300 p-5 text-sm text-neutral-700 dark:border-neutral-700 dark:text-neutral-200">
                      <p>{detail.viewUrl ? 'The photo could not be shown. Photo links stay valid for five minutes.' : 'No photo is available for this proof.'}</p>
                      {detail.viewUrl && <button type="button" onClick={loadDetail} className={`mt-3 inline-flex min-h-11 items-center gap-2 rounded-xl border border-neutral-300 px-4 font-semibold hover:bg-neutral-100 dark:border-neutral-700 dark:hover:bg-neutral-800 ${focusRing}`}><RefreshCw className="h-4 w-4" aria-hidden="true" />Load the photo again</button>}
                    </div>
                  )}
                </div>

                <div className="min-w-0 space-y-4">
                  {flagReasons.length > 0 && (
                    <div className="rounded-xl border border-warning bg-warning-subtle p-3 text-sm text-warning">
                      <p className="font-semibold">Why this proof is flagged</p>
                      <ul className="mt-1 list-disc space-y-1 pl-5">{flagReasons.map((reason) => <li key={reason}>{reason}</li>)}</ul>
                    </div>
                  )}
                  <dl className="grid gap-x-5 gap-y-3 sm:grid-cols-2">
                    <Fact term="Received by">{receivedBy(detail)}</Fact>
                    <Fact term="Relationship to customer">{detail.recipientRelationship}</Fact>
                    <Fact term="Where it was handed over">{detail.receivingLocation}</Fact>
                    <Fact term="Distance from delivery address">{meters(detail.proximityM) || 'Not measured'}</Fact>
                    <Fact term="Location accuracy">{detail.accuracyM == null ? 'No location reading' : `Within about ${meters(detail.accuracyM)}`}</Fact>
                    <Fact term="Rider's note">{detail.recipientNote}</Fact>
                    <Fact term="Authorization shown">{detail.authorizationEvidence}</Fact>
                    {detail.status !== 'FINALIZED' && <Fact term={detail.status === 'APPROVED' ? 'Approved' : 'Rejected'}>{dateTime(detail.reviewedAt)}</Fact>}
                    {detail.status !== 'FINALIZED' && <Fact term="Review note">{detail.reviewNote}</Fact>}
                  </dl>
                  {canReadParcels && detail.shipmentId && <Link to={`/business/delivery?view=parcels&parcel=${detail.shipmentId}`} className={`inline-flex min-h-9 items-center text-sm font-semibold underline underline-offset-4 ${focusRing}`}>Open the parcel</Link>}
                </div>
              </div>

              {detail.status === 'FINALIZED' && (
                <form onSubmit={(event) => event.preventDefault()} className="border-t border-neutral-200 p-4 dark:border-neutral-800">
                  <h3 className="font-semibold">{detail.reviewRequired ? 'Record your review' : 'Optional review'}</h3>
                  <p className="mt-1 max-w-2xl text-sm text-neutral-600 dark:text-neutral-300">
                    This delivery is already complete. Your decision is kept as a record only: it does not change the order, the parcel, or any payment. A rejection is logged as an exception together with your note.
                  </p>
                  <label className="mt-3 block text-sm font-medium" htmlFor="proof-review-note">Note <span className="font-normal text-neutral-600 dark:text-neutral-400">(needed to reject)</span></label>
                  <textarea id="proof-review-note" value={note} onChange={(event) => setNote(event.target.value)} onBlur={() => setNoteTouched(Boolean(note))}
                    maxLength={NOTE_MAX} rows={3} disabled={busy} aria-describedby="proof-review-note-help"
                    aria-invalid={noteTouched && noteTooShort ? 'true' : undefined}
                    className={`mt-1 w-full rounded-xl border border-neutral-300 bg-white p-3 text-sm dark:border-neutral-700 dark:bg-neutral-950 ${focusRing}`} />
                  <p id="proof-review-note-help" className={`mt-1 text-xs ${noteTouched && noteTooShort ? 'font-semibold text-danger' : 'text-neutral-600 dark:text-neutral-400'}`}>
                    {noteTouched && noteTooShort ? `To reject, explain what is wrong in at least ${NOTE_MIN} characters.` : `Up to ${NOTE_MAX} characters. Stored with the decision.`}
                  </p>
                  {decisionError && <p role="alert" className="mt-3 flex gap-2 rounded-xl border border-danger bg-danger-subtle p-3 text-sm text-danger"><AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />{decisionError}</p>}
                  <div className="mt-3 flex flex-wrap gap-3">
                    <button type="button" onClick={() => decide('APPROVED')} disabled={busy} className={`business-primary-action inline-flex min-h-11 items-center gap-2 rounded-xl bg-accent px-4 text-sm font-semibold text-on-accent hover:bg-accent-hover active:bg-accent-active focus-visible:ring-offset-2 disabled:opacity-50 ${focusRing}`}>{busy ? <Loader2 className="h-4 w-4 animate-spin motion-reduce:animate-none" aria-hidden="true" /> : <CheckCircle2 className="h-4 w-4" aria-hidden="true" />}Approve proof</button>
                    <button type="button" onClick={() => decide('REJECTED')} disabled={busy} className="inline-flex min-h-11 items-center gap-2 rounded-xl border border-default px-4 text-sm font-semibold text-foreground hover:border-strong hover:bg-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus disabled:opacity-50"><XCircle className="h-4 w-4" aria-hidden="true" />Reject proof</button>
                  </div>
                </form>
              )}
            </article>
          )}
        </section>
      </div>
    </main>
  );
}
